
import { useState } from 'react';
import { CheckCircle2, Mail, Copy, ExternalLink, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface InviteLinkDisplayProps {
    inviteToken?: string;
    inviteUrl?: string;
    displayName: string;
    email?: string;
    onClose?: () => void;
}

export function InviteLinkDisplay({ inviteToken, inviteUrl, displayName, email, onClose }: InviteLinkDisplayProps) {
    const [copied, setCopied] = useState(false);
    const link = inviteUrl || (inviteToken ? `${window.location.origin}/invite?token=${inviteToken}` : '');

    const copyText = async (text: string): Promise<boolean> => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                return true;
            }
        } catch (_err) {
            // fall through to legacy copy
        }

        try {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            const copiedLegacy = document.execCommand('copy');
            document.body.removeChild(textarea);
            return copiedLegacy;
        } catch (_err) {
            return false;
        }
    };

    const handleCopy = async () => {
        if (!link) {
            toast({
                title: "Invite link unavailable",
                description: "Could not resolve a valid invitation link.",
                variant: "destructive"
            });
            return;
        }

        const copiedSuccessfully = await copyText(link);
        if (!copiedSuccessfully) {
            window.prompt('Copy this invitation link:', link);
            toast({
                title: "Copy manually",
                description: "Clipboard access is blocked in this browser context.",
                variant: "destructive"
            });
            return;
        }

        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast({ title: "Link Copied", description: "The invitation link is ready to share." });
    };

    const handleEmail = async () => {
        if (!link) {
            toast({
                title: "Invite link unavailable",
                description: "Could not resolve a valid invitation link.",
                variant: "destructive"
            });
            return;
        }

        const subject = "Invitation to Adaptive Endurance";
        const body = `Hi ${displayName.split(' ')[0]},\n\nI've set up your profile on Adaptive Endurance. Please use the following link to connect your Intervals.icu account and get started:\n\n${link}\n\nThis link is unique to you and will securely link your data.\n\nBest,\nAdaptive Endurance Coach`;
        const emailTemplate = `Subject: ${subject}\n\n${body}`;
        const copiedSuccessfully = await copyText(emailTemplate);

        try {
            window.location.href = `mailto:${email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        } catch (_err) {
            // mail client invocation is best-effort
        }

        if (copiedSuccessfully) {
            toast({ title: "Email template copied", description: "Opening your default email client..." });
        } else {
            window.prompt('Copy this email template:', emailTemplate);
            toast({
                title: "Copy manually",
                description: "Email template opened, but clipboard access was blocked.",
                variant: "destructive"
            });
        }
    };

    return (
        <div className="space-y-6 py-2">
            <div className="flex flex-col items-center text-center space-y-3 mb-2">
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center border border-green-100 shadow-sm animate-in zoom-in duration-300">
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
                <div className="space-y-1">
                    <h3 className="text-xl font-bold text-slate-900 leading-none">Invitation Ready!</h3>
                    <p className="text-sm text-muted-foreground">For {displayName}</p>
                </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 overflow-hidden shadow-inner space-y-3">
                <div className="flex items-center justify-between px-1">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Magic Access Link</Label>
                    <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-[10px] font-semibold text-green-600 uppercase tracking-wider">Active</span>
                    </div>
                </div>

                <div className="flex items-center gap-2 group">
                    <div className="flex-1 bg-white border border-slate-200 px-3 py-2.5 rounded-xl text-xs font-mono break-all text-slate-600 group-hover:border-indigo-200 transition-colors">
                        {link}
                    </div>
                    <Button
                        size="icon"
                        variant="secondary"
                        onClick={() => void handleCopy()}
                        className={cn(
                            "h-10 w-10 shrink-0 rounded-xl transition-all",
                            copied ? "bg-green-500 text-white hover:bg-green-600" : "bg-white border border-slate-200 hover:border-indigo-200"
                        )}
                    >
                        {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <Button
                    variant="outline"
                    className="h-12 rounded-xl gap-2 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                    onClick={() => void handleEmail()}
                >
                    <Mail className="w-4 h-4" />
                    Send via Email
                </Button>
                <Button
                    variant="default"
                    className="h-12 rounded-xl gap-2 bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-200/50"
                    onClick={() => {
                        void handleCopy();
                        if (onClose) onClose();
                    }}
                >
                    <ExternalLink className="w-4 h-4" />
                    Copy & Close
                </Button>
            </div>

            <div className="flex items-center gap-2 p-3 bg-amber-50 text-amber-700 rounded-xl border border-amber-100 text-[11px] leading-relaxed">
                <Activity className="w-4 h-4 shrink-0 text-amber-500" />
                <p>
                    <strong>Coach Tip:</strong> Send this link directly. They will be prompted to connect Intervals.icu and then automatically logged in.
                </p>
            </div>
        </div>
    );
}
